// src/components/NoWorkDaysModal.jsx
import React, { useState } from 'react';
import { X, Plus, Trash2, Calendar } from 'lucide-react';

export default function NoWorkDaysModal({ 
  noWorkDays, 
  onClose, 
  onAddNoWorkDay, 
  onDeleteNoWorkDay,
  showToast 
}) {
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newDate) {
      if (showToast) showToast('Please select a date', 'error');
      return;
    }

    // Check if date already exists
    if (noWorkDays.some(nwd => nwd.date === newDate)) {
      if (showToast) showToast('This date is already marked as a no-work day', 'error');
      return;
    }

    setAdding(true);
    try {
      await onAddNoWorkDay(newDate, newReason.trim() || 'No Work');
      setNewDate('');
      setNewReason('');
      if (showToast) showToast('No-work day added successfully!', 'success');
    } catch (error) {
      console.error('Error adding no-work day:', error);
      if (showToast) showToast('Error adding no-work day', 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await onDeleteNoWorkDay(id);
      if (showToast) showToast('No-work day removed', 'success');
    } catch (error) {
      console.error('Error deleting no-work day:', error);
      if (showToast) showToast('Error removing no-work day', 'error');
    }
  };

  // Sort no work days by date
  const sortedNoWorkDays = [...noWorkDays].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );

  // Group by upcoming vs past
  const today = new Date().toISOString().split('T')[0];
  const upcomingDays = sortedNoWorkDays.filter(nwd => nwd.date >= today);
  const pastDays = sortedNoWorkDays.filter(nwd => nwd.date < today);

  return (
    <div className="modal-backdrop">
      <div className="modal-container no-work-modal">
        {/* Header */}
        <div className="no-work-modal-header">
          <div className="no-work-modal-header-content">
            <Calendar size={24} className="no-work-icon" />
            <h2 className="modal-title">Manage No-Work Days</h2>
          </div>
          <button onClick={onClose} className="modal-close-btn">
            <X size={20} />
          </button>
        </div>

        <p className="no-work-description">
          Mark days when no work will be scheduled (holidays, weather days, etc.)
        </p>

        {/* Add New No-Work Day */}
        <div className="no-work-add-section">
          <h3 className="modal-subtitle">Add No-Work Day</h3>
          <div className="no-work-form-grid">
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="form-input"
              min={new Date().toISOString().split('T')[0]}
            />
            <input
              type="text"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="Reason (optional)"
              className="form-input"
              maxLength={50}
            />
            <button
              onClick={handleAdd}
              disabled={adding || !newDate}
              className="save-btn no-work-add-btn"
            >
              <Plus size={16} />
              <span>{adding ? 'Adding...' : 'Add Day'}</span>
            </button>
          </div>
        </div>

        {/* Upcoming No-Work Days */}
        {upcomingDays.length > 0 && (
          <div className="no-work-section">
            <h3 className="modal-subtitle">
              Upcoming ({upcomingDays.length})
            </h3>
            <div className="no-work-list">
              {upcomingDays.map(nwd => (
                <div key={nwd.id} className="no-work-item upcoming">
                  <div className="no-work-item-content">
                    <div className="no-work-date">
                      {new Date(nwd.date + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    {nwd.reason && (
                      <div className="no-work-reason">{nwd.reason}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(nwd.id)}
                    className="delete-btn no-work-delete-btn"
                    title="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past No-Work Days */}
        {pastDays.length > 0 && (
          <div className="no-work-section">
            <h3 className="modal-subtitle">
              Past ({pastDays.length})
            </h3>
            <div className="no-work-list past">
              {pastDays.map(nwd => (
                <div key={nwd.id} className="no-work-item past">
                  <div className="no-work-item-content">
                    <div className="no-work-date">
                      {new Date(nwd.date + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    {nwd.reason && (
                      <div className="no-work-reason">{nwd.reason}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(nwd.id)}
                    className="no-work-past-delete-btn"
                    title="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {noWorkDays.length === 0 && (
          <div className="no-work-empty-state">
            <Calendar size={48} className="no-work-empty-icon" />
            <p className="no-work-empty-text">No blocked days yet</p>
            <p className="no-work-empty-subtext">Add days when work should not be scheduled</p>
          </div>
        )}

        {/* Footer Actions */}
        <div className="modal-actions">
          <button onClick={onClose} className="modal-cancel-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}